import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useEffect, useState } from 'react';
import {
    Link,
    useLocation,
    useSearchParams,
} from 'react-router-dom';
import {
    getSiteLocaleFromPathname,
    isValidEmail,
    withSiteLocale,
} from '../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import Button from '../common/Button/Button';
import TextInput from '../common/TextInput/TextInput';
import { GenericError, Icon, TextField, Title } from '../common';
import { getConfirmEmailCode, login } from '../../api/auth';
import { getCurrentUser } from '../../store/auth/authSlice';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import useSeo from '../../hooks/useSeo';
import { COLOURS } from '../../utils/consts';
import EmailCode from '../EmailCode/EmailCode';

import './login.css';
import { GoogleAuthButton } from '../GoogleAuthButton/GoogleAuthButton';

interface LoginFormValues {
    email: string | null;
    password: string | null;
}

interface Errors {
    email: string | null;
    password: string | null;
}

const Login = () => {
    const auth = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useLocalizedNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const locale = getSiteLocaleFromPathname(location.pathname);
    useSeo({
        title: t('seo.login.title'),
        description: t('seo.login.description'),
        path: '/login',
        noIndex: true,
    });
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [showEmailCodeForm, setShowEmailCodeForm] = useState(false);

    const from =
        ((location.state as any)?.from && `${(location.state as any).from.pathname}${(location.state as any).from.search || ''}${(location.state as any).from.hash || ''}`) ||
        searchParams.get('from') ||
        '/home';
    const showEmailConfirmed = searchParams.get('email-confirmed');

    useEffect(() => {
        if (auth.authData) {
            navigate(from, { replace: true });
        }
    }, [auth, from, navigate]);

    useEffect(() => {
        if (searchParams.get('email-confirmed')) {
            setShowEmailCodeForm(false);
            setAuthError(null);
        }
    }, [searchParams]);

    const [formValues, setFormValues] = useState<LoginFormValues>({
        email: null,
        password: null,
    });
    const [errors, setErrors] = useState<Errors>({
        email: null,
        password: null,
    });

    const inputChangeHandler = (name: string, value: string) => {
        setFormValues({
            ...formValues,
            [name]: value,
        });
        setErrors({
            email: null,
            password: null,
        });
    };

    const onLoginHandler = async () => {
        if (
            !formValues.email ||
            (formValues.email && !isValidEmail(formValues.email))
        ) {
            setErrors({ ...errors, email: t('errors.invalidEmail') });
            return;
        }

        if (!formValues.password) {
            setErrors({
                ...errors,
                password: t('errors.invalidPassword'),
            });
            return;
        }

        try {
            setIsLoading(true);
            await login(formValues.email, formValues.password);

            dispatch(getCurrentUser());

            setIsLoading(false);
        } catch (e: any) {
            if (e.response.status === 401) {
                setAuthError(t('errors.invalidEmailOrPassword'));
            } else if (e.response.status === 403) {
                setShowEmailCodeForm(true);

                try {
                    await getConfirmEmailCode(formValues.email);
                } catch (e) {
                    return;
                }
            } else if (e.response.status === 429) {
                setAuthError(t('errors.loginAttemtps'));
            } else {
                setShowError(true);
            }
            setIsLoading(false);
        }
    };

    if (showError) {
        return <GenericError />;
    }

    return (
        <div className="login-wrapper">
            {showEmailCodeForm ? (
                <EmailCode email={formValues.email as string} />
            ) : (
                <div className="login-main">
                    <div className="login-header">
                        <Title text={t('login.title')} size="xl" />
                    </div>
                    {!showEmailConfirmed && <GoogleAuthButton />}
                    {showEmailConfirmed && (
                        <div className="email-confirmed">
                            <div>
                                <Title text={t('login.emailConfirmed')}></Title>
                                <Icon
                                    name="bx-check-circle"
                                    color={COLOURS.GREEN}
                                    size="30px"
                                />
                            </div>
                            <TextField>{t('login.emailVerified')}</TextField>
                        </div>
                    )}
                    <div className="login-inputs">
                        <TextInput
                            label="Email"
                            name="email"
                            placeholder={t('login.enterEmail')}
                            onChange={inputChangeHandler}
                            value={formValues.email}
                            error={errors.email}
                            className="full-width"
                            enableAutocomplete
                        />
                        <TextInput
                            label="Password"
                            name="password"
                            placeholder={t('login.enterPassword')}
                            onChange={inputChangeHandler}
                            value={formValues.password}
                            error={errors.password}
                            className="full-width"
                            type="password"
                            enableAutocomplete
                        />
                    </div>

                    <div className="login-forgot-password">
                        {authError && (
                            <div className="login-error">
                                <Icon
                                    name="bx-error"
                                    color={COLOURS.RED}
                                    size="35px"
                                />
                                <TextField color="red">{authError}</TextField>
                            </div>
                        )}
                    </div>
                    <Button
                        text={t('login.login')}
                        variant="green"
                        onClick={onLoginHandler}
                        disabled={isLoading}
                    />
                    <div className="register-link" data-testid="register-link">
                        <Link to={withSiteLocale('/reset-password', locale)}>
                            <TextField>{t('common.forgotPassword')}</TextField>
                        </Link>
                        <Link to={withSiteLocale('/register', locale)}>
                            <TextField color="green" weight="bold">
                                {t('login.createAccount')}
                            </TextField>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
